/** Recorte de imágenes en el navegador (para las fotos de los eventos). */

export type Focus = { x: number; y: number }; // 0..1

export function loadImageFile(file: File): Promise<{ img: HTMLImageElement; url: string }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve({ img, url });
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("No se pudo leer la imagen."));
    };
    img.src = url;
  });
}

/** ¿Hay sobrante en horizontal ("x"), en vertical ("y") o encaja exacto (null)? */
export function overflowAxis(sw: number, sh: number, ratio: number): "x" | "y" | null {
  const r = sw / sh;
  if (Math.abs(r - ratio) < 0.01) return null;
  return r > ratio ? "x" : "y";
}

export function orientationOf(sw: number, sh: number): "horizontal" | "vertical" | "cuadrada" {
  const r = sw / sh;
  if (r > 1.12) return "horizontal";
  if (r < 0.89) return "vertical";
  return "cuadrada";
}

/** Recorta la imagen a la proporción dada (centrada según `focus`) y la devuelve comprimida. */
export async function cropToBlob(img: HTMLImageElement, ratio: number, maxWidth: number, focus: Focus): Promise<Blob> {
  const sw = img.naturalWidth;
  const sh = img.naturalHeight;
  let cw: number;
  let ch: number;
  let sx = 0;
  let sy = 0;
  if (sw / sh > ratio) {
    ch = sh;
    cw = sh * ratio;
    sx = (sw - cw) * focus.x;
  } else {
    cw = sw;
    ch = sw / ratio;
    sy = (sh - ch) * focus.y;
  }
  const outW = Math.round(Math.min(maxWidth, cw));
  const outH = Math.round(outW / ratio);
  const canvas = document.createElement("canvas");
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Tu navegador no puede procesar la imagen.");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, sx, sy, cw, ch, 0, 0, outW, outH);

  const toBlob = (type: string, q: number) => new Promise<Blob | null>((res) => canvas.toBlob(res, type, q));
  const webp = await toBlob("image/webp", 0.85);
  if (webp && webp.type === "image/webp") return webp;
  const jpeg = await toBlob("image/jpeg", 0.88);
  if (jpeg) return jpeg;
  throw new Error("No se pudo preparar la imagen.");
}

/** Reduce la imagen (sin recortar) a un máximo de `maxSide` px en su lado mayor y la comprime. */
export async function resizeToBlob(img: HTMLImageElement, maxSide: number): Promise<Blob> {
  const sw = img.naturalWidth;
  const sh = img.naturalHeight;
  const scale = Math.min(1, maxSide / Math.max(sw, sh));
  const w = Math.round(sw * scale);
  const h = Math.round(sh * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Tu navegador no puede procesar la imagen.");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, w, h);
  const toBlob = (type: string, q: number) => new Promise<Blob | null>((res) => canvas.toBlob(res, type, q));
  const webp = await toBlob("image/webp", 0.85);
  if (webp && webp.type === "image/webp") return webp;
  const jpeg = await toBlob("image/jpeg", 0.88);
  if (jpeg) return jpeg;
  throw new Error("No se pudo preparar la imagen.");
}
